import React from 'react';
import { Field } from 'redux-form';
import { months } from '../../../../Utils/months';
import { TYPE_OF_FLOW } from '../../../../../../utility/constants';
import { renderCurrency, renderDollar } from './RenderField';
import NumberFormat from 'react-number-format';

// Función para formatear moneda con soporte para negativos con paréntesis
export const formatComparativoCurrency = (value, currency, showNegativeInParentheses = false) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '';
    }
    
    const numericValue = parseFloat(value) || 0;
    const prefix = currency === 'USD' ? '$ ' : 'Q ';
    
    if (showNegativeInParentheses && numericValue < 0) {
        return `${prefix}(${Math.abs(numericValue).toLocaleString('en-US', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        })})`;
    }
    
    return `${prefix}${numericValue.toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
    })}`;
};

// Función para obtener el valor formateado con color
export const formatComparativoCurrencyWithColor = (value, currency, showNegativeInParentheses = false) => {
    if (value === null || value === undefined || isNaN(value)) {
        return { text: '', color: 'inherit' };
    }
    
    const numericValue = parseFloat(value) || 0;
    const prefix = currency === 'USD' ? '$ ' : 'Q ';
    
    // Determinar color: rojo para negativos, negro para positivos
    const color = numericValue < 0 ? '#dc3545' : 'inherit';
    
    let text;
    if (showNegativeInParentheses && numericValue < 0) {
        text = `${prefix}(${Math.abs(numericValue).toLocaleString('en-US', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        })})`;
    } else {
        text = `${prefix}${numericValue.toLocaleString('en-US', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        })}`;
    }
    
    return { text, color };
};

// Hook para cálculos locales (similar a useSummaryTotalsLocal)
export const useComparativoTotalsLocal = (allValues) => {
    return React.useMemo(() => {
        if (!allValues) return null;
        
        const { investments, operation, financial, summary } = allValues;
        
        // Calcular totales por mes y tipo
        const calculateTotals = () => {
            const totals = {
                entradas: {},
                salidas: {},
                flujoNeto: {},
                saldoFinal: {},
                operativosEntradas: {},
                operativosSalidas: {},
                operativosNeto: {},
                financierosEntradas: {},
                financierosSalidas: {},
                financierosNeto: {},
                inversionesNeto: {}
            };
            
            // Inicializar totales
            [...months, 'total'].forEach(period => {
                Object.keys(totals).forEach(key => {
                    totals[key][period] = 0;
                });
            });
            
            // Procesar flujos operativos
            if (operation && Array.isArray(operation)) {
                operation.forEach(item => {
                    if (item.isTitulo) return;
                    
                    months.forEach(month => {
                        const value = parseFloat(item[month]) || 0;
                        
                        if (item.type_of_flow === TYPE_OF_FLOW.INGRESOS || (item.type_of_flow !== TYPE_OF_FLOW.SALIDAS && value > 0)) {
                            totals.operativosEntradas[month] += value;
                            totals.entradas[month] += value;
                        } else if (item.type_of_flow === TYPE_OF_FLOW.SALIDAS || value < 0) {
                            totals.operativosSalidas[month] += Math.abs(value);
                            totals.salidas[month] += Math.abs(value);
                        }
                    });
                });
            }
            
            // Procesar flujos financieros
            if (financial && Array.isArray(financial)) {
                financial.forEach(item => {
                    months.forEach(month => {
                        const value = parseFloat(item[month]) || 0;
                        
                        if (item.type_of_flow === TYPE_OF_FLOW.INGRESOS || (item.type_of_flow !== TYPE_OF_FLOW.SALIDAS && value > 0)) {
                            totals.financierosEntradas[month] += value;
                            totals.entradas[month] += value;
                        } else if (item.type_of_flow === TYPE_OF_FLOW.SALIDAS || value < 0) {
                            totals.financierosSalidas[month] += Math.abs(value);
                            totals.salidas[month] += Math.abs(value);
                        }
                    });
                });
            }
            
            // Procesar inversiones (generalmente son salidas)
            if (investments && Array.isArray(investments)) {
                investments.forEach(item => {
                    months.forEach(month => {
                        const value = parseFloat(item[month]) || 0;
                        totals.inversionesNeto[month] += value;
                        
                        if (value > 0) {
                            totals.entradas[month] += value;
                        } else {
                            totals.salidas[month] += Math.abs(value);
                        }
                    });
                });
            }
            
            // Calcular netos y saldos
            months.forEach(month => {
                totals.operativosNeto[month] = totals.operativosEntradas[month] - totals.operativosSalidas[month];
                totals.financierosNeto[month] = totals.financierosEntradas[month] - totals.financierosSalidas[month];
                totals.flujoNeto[month] = totals.entradas[month] - totals.salidas[month];
            });
            
            // Calcular saldo final de forma acumulativa
            let saldoAcumulado = 0;
            
            months.forEach((month, index) => {
                if (index === 0) {
                    // Para enero: Saldo Inicial (del backend) + Flujo Neto de Enero
                    const saldoInicial = (summary && summary[0] && parseFloat(summary[0].enero)) || 0;
                    saldoAcumulado = saldoInicial + totals.flujoNeto[month];
                } else {
                    // Para febrero en adelante: Saldo Final del mes anterior + Flujo Neto del mes actual
                    saldoAcumulado = saldoAcumulado + totals.flujoNeto[month];
                }
                
                totals.saldoFinal[month] = saldoAcumulado;
            });
            
            // Calcular totales anuales
            Object.keys(totals).forEach(key => {
                totals[key].total = months.reduce((sum, month) => sum + totals[key][month], 0);
            });
            
            return totals;
        };
        
        return calculateTotals();
    }, [allValues]);
};

// Componente para mostrar valores calculados localmente
export const LocalComparativoCurrencyDisplay = ({ value, currency, showNegativeInParentheses = false }) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '';
    }
    
    return formatComparativoCurrency(value, currency, showNegativeInParentheses);
};

// Función para calcular totales usando la lógica del hook
const calculateComparativoTotals = (allValues) => {
    if (!allValues) return null;
    
    const { investments, operation, financial, summary } = allValues;
    
    // Calcular totales por mes y tipo
    const totals = {
        entradas: {},
        salidas: {},
        flujoNeto: {},
        saldoFinal: {},
        operativosEntradas: {},
        operativosSalidas: {},
        operativosNeto: {},
        financierosEntradas: {},
        financierosSalidas: {},
        financierosNeto: {},
        inversionesNeto: {}
    };
    
    // Inicializar totales
    [...months, 'total'].forEach(period => {
        Object.keys(totals).forEach(key => {
            totals[key][period] = 0;
        });
    });
    
    // Procesar flujos operativos
    if (operation && Array.isArray(operation)) {
        operation.forEach(item => {
            if (item.isTitulo) return;
            
            months.forEach(month => {
                const value = parseFloat(item[month]) || 0;
                
                if (item.type_of_flow === TYPE_OF_FLOW.INGRESOS || (item.type_of_flow !== TYPE_OF_FLOW.SALIDAS && value > 0)) {
                    totals.operativosEntradas[month] += value;
                    totals.entradas[month] += value;
                } else if (item.type_of_flow === TYPE_OF_FLOW.SALIDAS || value < 0) {
                    totals.operativosSalidas[month] += Math.abs(value);
                    totals.salidas[month] += Math.abs(value);
                }
            });
        });
    }
    
    // Procesar flujos financieros
    if (financial && Array.isArray(financial)) {
        financial.forEach(item => {
            months.forEach(month => {
                const value = parseFloat(item[month]) || 0;
                
                if (item.type_of_flow === TYPE_OF_FLOW.INGRESOS || (item.type_of_flow !== TYPE_OF_FLOW.SALIDAS && value > 0)) {
                    totals.financierosEntradas[month] += value;
                    totals.entradas[month] += value;
                } else if (item.type_of_flow === TYPE_OF_FLOW.SALIDAS || value < 0) {
                    totals.financierosSalidas[month] += Math.abs(value);
                    totals.salidas[month] += Math.abs(value);
                }
            });
        });
    }
    
    // Procesar inversiones (generalmente son salidas)
    if (investments && Array.isArray(investments)) {
        investments.forEach(item => {
            months.forEach(month => {
                const value = parseFloat(item[month]) || 0;
                totals.inversionesNeto[month] += value;
                
                if (value > 0) {
                    totals.entradas[month] += value;
                } else {
                    totals.salidas[month] += Math.abs(value);
                }
            });
        });
    }
    
    // Calcular netos y saldos
    months.forEach(month => {
        totals.operativosNeto[month] = totals.operativosEntradas[month] - totals.operativosSalidas[month];
        totals.financierosNeto[month] = totals.financierosEntradas[month] - totals.financierosSalidas[month];
        totals.flujoNeto[month] = totals.entradas[month] - totals.salidas[month];
    });
    
    // Calcular saldo final de forma acumulativa
    let saldoAcumulado = 0;
    
    months.forEach((month, index) => {
        if (index === 0) {
            // Para enero: Saldo Inicial (del backend) + Flujo Neto de Enero
            const saldoInicial = (summary && summary[0] && parseFloat(summary[0].enero)) || 0;
            saldoAcumulado = saldoInicial + totals.flujoNeto[month];
        } else {
            // Para febrero en adelante: Saldo Final del mes anterior + Flujo Neto del mes actual
            saldoAcumulado = saldoAcumulado + totals.flujoNeto[month];
        }
        
        totals.saldoFinal[month] = saldoAcumulado;
    });
    
    // Calcular totales anuales
    Object.keys(totals).forEach(key => {
        totals[key].total = months.reduce((sum, month) => sum + totals[key][month], 0);
    });
    
    return totals;
};

// Función para actualizar totales solo al guardar (no en tiempo real)
export const updateComparativoSummaryOnSave = (allValues, change) => {
    if (!allValues) return;
    
    const totals = calculateComparativoTotals(allValues);
    if (!totals) return;
    
    // Índices del resumen
    const SALDO_INICIAL_INDEX = 0;
    const ENTRADAS_INDEX = 1;
    const SALIDAS_INDEX = 2;
    const FLUJO_NETO_INDEX = 3;
    const SALDO_FINAL_INDEX = 4;
    
    // Actualizar campos del resumen solo al guardar
    months.forEach(month => {
        change(`summary.${ENTRADAS_INDEX}.${month}`, totals.entradas[month]);
        change(`summary.${SALIDAS_INDEX}.${month}`, -totals.salidas[month]); // Negativo para salidas
        change(`summary.${FLUJO_NETO_INDEX}.${month}`, totals.flujoNeto[month]);
        change(`summary.${SALDO_FINAL_INDEX}.${month}`, totals.saldoFinal[month]);
    });
};

// Componente para botones de agregar ingreso/salida (mejorado)
export const AddTransactionButtons = ({ fields, setCollapsed, transactionType = "FINANCIAL" }) => {
    const handleButtonHover = (e, color) => {
        e.target.style.backgroundColor = color;
        e.target.style.color = 'white';
        e.target.style.transform = 'scale(1.02)';
    };

    const handleButtonLeave = (e, color) => {
        e.target.style.backgroundColor = 'transparent';
        e.target.style.color = color;
        e.target.style.transform = 'scale(1)';
    };

    const buttonStyle = (color) => ({
        color: color,
        fontWeight: 'bold',
        borderColor: color,
        padding: '8px 16px',
        fontSize: '14px'
    });

    // Mapeo de tipos de transacción para el comparativo
    const getTypeOfRow = () => {
        switch(transactionType) {
            case "FINANCIAL": return 1; // RegistroComparativoFlujoCaja.FINANCIAL
            case "OPERATING": return 2; // RegistroComparativoFlujoCaja.OPERATING
            case "INVESTMENTS": return 3; // RegistroComparativoFlujoCaja.INVESTMENTS
            default: return 1;
        }
    };

    const agregarIngreso = () => {
        fields.push({
            isNew: true,
            is_manual: true,
            is_dollar: true,
            name: '',
            type_of_row: getTypeOfRow(),
            activo: true,
            type_of_flow: TYPE_OF_FLOW.INGRESOS, // 30 - INCOME según modelo backend
            enero: 0,
            febrero: 0,
            marzo: 0,
            abril: 0,
            mayo: 0,
            junio: 0,
            julio: 0,
            agosto: 0,
            septiembre: 0,
            octubre: 0,
            noviembre: 0,
            diciembre: 0,
            total: 0
        });
        setCollapsed(false);
    };

    const agregarSalida = () => {
        fields.push({
            isNew: true,
            is_manual: true,
            is_dollar: true,
            name: '',
            type_of_row: getTypeOfRow(),
            activo: true,
            type_of_flow: TYPE_OF_FLOW.SALIDAS, // 40 - EXPENDITURE según modelo backend
            enero: 0,
            febrero: 0,
            marzo: 0,
            abril: 0,
            mayo: 0,
            junio: 0,
            julio: 0,
            agosto: 0,
            septiembre: 0,
            octubre: 0,
            noviembre: 0,
            diciembre: 0,
            total: 0
        });
        setCollapsed(false);
    };

    return (
        <div className="mt-2">
            <div className="d-flex justify-content-between">
                <div 
                    type="button" 
                    className="btn btn-outline-success flex-fill mr-2 d-flex justify-content-center align-items-center agregar-tab-generica"
                    style={buttonStyle('#28a745')}
                    onMouseEnter={(e) => handleButtonHover(e, '#28a745')}
                    onMouseLeave={(e) => handleButtonLeave(e, '#28a745')}
                    onClick={agregarIngreso}>
                    + AGREGAR INGRESO
                </div>
                <div 
                    type="button" 
                    className="btn btn-outline-danger flex-fill ml-2 d-flex justify-content-center align-items-center agregar-tab-generica"
                    style={buttonStyle('#dc3545')}
                    onMouseEnter={(e) => handleButtonHover(e, '#dc3545')}
                    onMouseLeave={(e) => handleButtonLeave(e, '#dc3545')}
                    onClick={agregarSalida}>
                    + AGREGAR SALIDA
                </div>
            </div>
        </div>
    );
};

// Función para separar campos por tipo (mejorada) - para FieldArray
export const separateFieldsByType = (fields) => {
    const allFields = fields.getAll() || [];
    const ingresos = [];
    const salidas = [];

    allFields.forEach((field, index) => {
        if (field.isTitulo) return; // Para evitar títulos en operativos
        
        // Prioridad 1: Usar type_of_flow numérico del backend
        if (field.type_of_flow === TYPE_OF_FLOW.SALIDAS) { // 40 - EXPENDITURE
            salidas.push({ field, index });
            return;
        }
        if (field.type_of_flow === TYPE_OF_FLOW.INGRESOS) { // 30 - INCOME
            ingresos.push({ field, index });
            return;
        }
        
        // Compatibilidad con identificadores antiguos (transactionType)
        if (field.transactionType === 'SALIDA') {
            salidas.push({ field, index });
            return;
        }
        if (field.transactionType === 'INGRESO') {
            ingresos.push({ field, index });
            return;
        }
        
        // Prioridad 2: Determinar por valores (para registros existentes)
        const hasNegativeValues = months.some(month => (parseFloat(field[month]) || 0) < 0);
        const hasPositiveValues = months.some(month => (parseFloat(field[month]) || 0) > 0);
        
        if (hasNegativeValues && !hasPositiveValues) {
            salidas.push({ field, index });
        } else {
            ingresos.push({ field, index });
        }
    });

    return { ingresos, salidas };
};

// Función para separar campos por tipo - para objetos planos
export const separateFieldsByTypeFromObject = (fieldsObject) => {
    const ingresos = [];
    const salidas = [];

    Object.entries(fieldsObject || {}).forEach(([fieldName, field]) => {
        if (field.isTitulo) return; // Para evitar títulos
        
        // Prioridad 1: Usar type_of_flow numérico del backend
        if (field.type_of_flow === TYPE_OF_FLOW.SALIDAS) { // 40 - EXPENDITURE
            salidas.push({ fieldName, field });
            return;
        }
        if (field.type_of_flow === TYPE_OF_FLOW.INGRESOS) { // 30 - INCOME
            ingresos.push({ fieldName, field });
            return;
        }
        
        // Compatibilidad con identificadores antiguos
        if (field.transactionType === 'expense' || field.transactionType === 'SALIDA') {
            salidas.push({ fieldName, field });
            return;
        }
        if (field.transactionType === 'income' || field.transactionType === 'INGRESO') {
            ingresos.push({ fieldName, field });
            return;
        }
        
        // Prioridad 2: Determinar por valores (para registros existentes)
        const hasNegativeValues = months.some(month => (parseFloat(field[month]) || 0) < 0);
        const hasPositiveValues = months.some(month => (parseFloat(field[month]) || 0) > 0);
        
        if (hasNegativeValues && !hasPositiveValues) {
            salidas.push({ fieldName, field });
        } else {
            ingresos.push({ fieldName, field });
        }
    });

    return { ingresos, salidas };
};

// Componente para renderizar headers de sección colapsables
export const renderCollapsibleSectionHeader = (
    title, 
    backgroundColor, 
    textColor, 
    items, 
    collapsed, 
    onToggle,
    totals = null
) => {
    const totalForSection = totals || items.reduce((total, { field }) => {
        return total + months.reduce((monthTotal, month) => {
            return monthTotal + (parseFloat(field[month]) || 0);
        }, 0);
    }, 0);
    
    return (
        <thead onClick={onToggle} style={{ cursor: 'pointer' }}>
            <tr style={{ backgroundColor, borderTop: '2px solid #dee2e6' }}>
                <th className='first-column table-form-fixed-cell-left-0 titulo-left-padded' style={{ color: textColor }}>
                    <div className="d-flex align-items-center">
                        <span className="mr-2" style={{ fontSize: '14px' }}>
                            {collapsed ? '▶' : '▼'}
                        </span>
                        {title}
                    </div>
                </th>
                <th className='table-form-fixed-cell-left-1 text-right-padded' style={{ color: textColor }}>
                    TOTAL
                </th>
                {months.map((month, index) => (
                    <th key={index} style={{ color: textColor, textAlign: 'center', padding: '8px' }}>
                        {index + 1}
                    </th>
                ))}
                <th style={{ color: textColor }}>ACCIONES</th>
            </tr>
        </thead>
    );
};

// Función para renderizar filas de totales de sección
export const renderSectionTotalRow = (title, backgroundColor, textColor, totalValue, monthlyTotals, currency) => {
    const totalFormatted = formatComparativoCurrencyWithColor(totalValue, currency, totalValue < 0);
    
    return (
        <tr style={{ backgroundColor, fontWeight: 'bold', borderTop: '2px solid #d4edda' }}>
            
            <td className='first-column table-form-fixed-cell-left-0' 
            style={{ 
                color: textColor, 
                paddingLeft: '20px',
                textAlign: 'left',
                borderLeft: `4px solid ${title === 'INGRESOS' ? '#28a745' : '#dc3545'}`
            }}>
                <div style={{ 
                    textAlign: 'left', 
                    width: '100%', 
                    color: textColor, 
                    fontWeight: 'bold',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center'
                }}>
                    {title}
                </div>
            </td>
            <td className='table-form-fixed-cell-left-1 text-right-padded' style={{ color: totalFormatted.color }}>
                {totalFormatted.text}
            </td>
            {months.map((month) => {
                const monthFormatted = formatComparativoCurrencyWithColor(monthlyTotals[month] || 0, currency, (monthlyTotals[month] || 0) < 0);
                return (
                    <td key={month} className="text-right-padded" style={{ color: monthFormatted.color }}>
                        {monthFormatted.text}
                    </td>
                );
            })}
            <td></td>
        </tr>
    );
};

// Función para renderizar fila de diferencia/total general
export const renderDifferenceRow = (title, totalValue, monthlyTotals, currency) => {
    const isNegative = totalValue < 0;
    const backgroundColor = isNegative ? '#f8d7da' : '#d4edda';
    const textColor = isNegative ? '#721c24' : '#155724';
    
    const totalFormatted = formatComparativoCurrencyWithColor(totalValue, currency, totalValue < 0);
    
    return (
        <tr className='celda-totales' style={{ fontWeight: 'bold', borderTop: '3px solid #6c757d' }}>
            <td className='table-form-fixed-cell-left-0 titulo-left-padded' style={{ 
                color: textColor, 
                paddingLeft: '20px',
                textAlign: 'left',
            }}>
                <div style={{ 
                    textAlign: 'left', 
                    width: '100%', 
                    color: textColor, 
                    fontWeight: 'bold',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center'
                }}>
                    {title}
                </div>
            </td>
            <td className='table-form-fixed-cell-left-1 text-right-padded' style={{ color: totalFormatted.color }}>
                {totalFormatted.text}
            </td>
            {months.map((month) => {
                const monthFormatted = formatComparativoCurrencyWithColor(monthlyTotals[month] || 0, currency, (monthlyTotals[month] || 0) < 0);
                return (
                    <td key={month} className="text-right-padded" style={{ color: monthFormatted.color }}>
                        {monthFormatted.text}
                    </td>
                );
            })}
            <td></td>
        </tr>
    );
}; 